/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { injectIntl } from 'react-intl';
import { useStaticQuery, graphql } from "gatsby"

const SEO = ({ description, intl, meta, title, image, children, article, author, additionalBreadcrumb, path }) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl
            title
            description
            author
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const defaultTitle = site.siteMetadata?.title
  const ogImage = image ? `${site.siteMetadata.siteUrl}${image}` : `${site.siteMetadata.siteUrl}/img/site-wepayout-placeholder.png`
  let schemaArticle = null
  
  const itemListElement = [
    {
      '@type': 'ListItem',
      item: {
        '@id': intl.locale === 'pt-br' ? `${site.siteMetadata.siteUrl}/pt-br` : site.siteMetadata.siteUrl,
        name: intl.locale === 'pt-br' ? 'Início' : 'Homepage',
      },
      position: 1,
    },
  ]

  const schemaWebsite = {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    name: 'WePayOut',
    url: path ? path : 'https://wepayout.co',
  }

  const schemaOrganization = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    url: site.siteMetadata.siteUrl,
    sameAs: [
      "https://www.facebook.com/wepayout/",
      "https://www.linkedin.com/company/wepayout/",
      "https://www.instagram.com/wepayout_oficial/",
      "https://www.youtube.com/channel/UCUoBiuKlYFkQGtqf8zD1bwg"
    ],
    legalName: "WE PAY OUT SERVICOS DE PAGAMENTOS LTDA",
    name: "WePayOut",
    logo: `${site.siteMetadata.siteUrl}/img/logo-index.png`,
    description: 'WePayOut is Brazilian fintech specialized in mass payouts in and to Brazil. A complete and diversified solution to pay contractors, freelancers, wages and salaries, remittances and any other disbursement type in Brazil through a variety of ways as bank transfer, cash pick up, wallet and instant payments.',
    founders: [
      {
        "@context": "http://schema.org",
        "@type": "Person",
        "name": "Fernanda Zago",
        "jobTitle": "CEO and Co-Founder",
        "image": "https://wepayout.co/fernanda.png",
        "sameAs":[
          "https://www.linkedin.com/in/fernanda-rachel-zago/"
        ]
  	  },
      {
        "@context": "http://schema.org",
        "@type": "Person",
        "name": "Matheus Nunes",
        "jobTitle": "CTO and Co-Founder",
        "image": "https://wepayout.co/matheus.png",
        "sameAs":[
          "https://www.linkedin.com/in/matheus-gobato-nunes/"
        ]
      },
    ]
  }

  if (article) {
    const [dayPublished, monthPublished, yearPublished] = article.frontmatter.date.split('/')

    schemaArticle = {
      '@context': 'http://schema.org',
      '@type': 'Article',
      author: {
        '@type': 'Person',
        name: author.name,
      },
      copyrightHolder: {
        '@type': 'Person',
        name: author.name,
      },
      copyrightYear: yearPublished,
      creator: {
        '@type': 'Person',
        name: author.name,
      },
      publisher: {
        '@type': 'Organization',
        name: site.siteMetadata.title,
        logo: {
          '@type': 'ImageObject',
          url: `${site.siteMetadata.siteUrl}/img/logo-index.png`,
        },
      },
      datePublished: `${yearPublished}-${monthPublished}-${dayPublished}`,
      dateModified: `${yearPublished}-${monthPublished}-${dayPublished}`,
      description: metaDescription,
      headline: title,
      inLanguage: intl.locale,
      url: `${site.siteMetadata.siteUrl}${article.fields.slug}`,
      name: title,
      image: {
        '@type': 'ImageObject',
        url: ogImage,
      },
      mainEntityOfPage: `${site.siteMetadata.siteUrl}${article.fields.slug}`,
      wordCount: article.wordCount.words
    }

    itemListElement.push({
      '@type': 'ListItem',
      item: {
        '@id': intl.locale === 'pt-br' ? `${site.siteMetadata.siteUrl}/pt-br/blog` : `${site.siteMetadata.siteUrl}/blog`,
        name: 'Blog',
      },
      position: 2,
    })

    itemListElement.push({
      '@type': 'ListItem',
      item: {
        '@id': `${site.siteMetadata.siteUrl}${article.fields.slug}`,
        name: title,
      },
      position: 3,
    })
  } else if (additionalBreadcrumb) {
    itemListElement.push({
      '@type': 'ListItem',
      item: {
        '@id': additionalBreadcrumb.url,
        name: additionalBreadcrumb.title,
      },
      position: 2,
    })
  }

  const breadcrumb = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    description: 'Breadcrumbs list',
    name: 'Breadcrumbs',
    itemListElement,
  }

  return (
    <Helmet
      htmlAttributes={{ lang: intl.locale }}
      title={title}
      titleTemplate={defaultTitle ? `%s - ${defaultTitle}` : null}
      meta={[
        { name: `description`, content: metaDescription },
        { property: `og:title`, content: title },
        { property: `og:description`, content: metaDescription },
        { property: `og:image`, content: ogImage },
        { property: `og:type`, content: `website` },
        { name: `twitter:card`, content: `summary_large_image` },
        { name: `twitter:image:src`, content: ogImage },
        { name: `twitter:creator`, content: site.siteMetadata?.author || `` },
        { name: `twitter:title`, content: title },
        { name: `twitter:description`, content: metaDescription },
      ].concat(meta)}
    >
      {article && <script type="application/ld+json">{JSON.stringify(schemaArticle)}</script>}
      {!article && <script type="application/ld+json">{JSON.stringify(schemaWebsite)}</script>}
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      <script type="application/ld+json">{JSON.stringify(schemaOrganization)}</script>
      {children}
    </Helmet>
  )
}

SEO.defaultProps = {
  meta: [],
  description: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
}

export default injectIntl(SEO) 